/**
 * WhatTheAiLearned (Plan v9 §4.2 prototype rework).
 *
 * Renders a 3-bullet list of clinic-specific learnings. Each bullet is
 * tagged with a closed-vocabulary category (preference, protocol,
 * outcome) so the SR experience labels each insight.
 *
 * Data source: `MetricsSnapshot.learningBulletsByTenant[tenantId]`.
 * Demo Mode supplies the canned baseline; Live Mode populates from a
 * derived telemetry view.
 *
 * HC2 token-only: every color, spacing, radius value resolves through
 * Signal DS tokens via Tailwind. No raw hex.
 * HC5: zero em / en dashes anywhere.
 * HC6 a11y: list is `role="list"`; each item is `role="listitem"`.
 *   The category badge is a plain text span so it announces with the
 *   bullet body.
 */
import type { LearningBullet, LearningCategory } from '../../services/metrics/types';

export interface WhatTheAiLearnedProps {
  /** Per-tenant list of learning bullets. */
  bullets: ReadonlyArray<LearningBullet>;
  /** Optional className for the outer wrapper. */
  className?: string;
}

const CATEGORY_LABEL: Record<LearningCategory, string> = {
  preference: 'Preference',
  protocol: 'Protocol',
  outcome: 'Outcome',
};

const CATEGORY_CLASSES: Record<LearningCategory, string> = {
  preference:
    'inline-flex items-center rounded-signal-pill bg-signal-primary-100 px-gutter-sm py-1 text-signal-xs font-semibold uppercase tracking-wide text-signal-primary-700',
  protocol:
    'inline-flex items-center rounded-signal-pill bg-warning-soft-bg px-gutter-sm py-1 text-signal-xs font-semibold uppercase tracking-wide text-warning-soft-fg',
  outcome:
    'inline-flex items-center rounded-signal-pill bg-status-active-bg px-gutter-sm py-1 text-signal-xs font-semibold uppercase tracking-wide text-status-active-fg',
};

export function WhatTheAiLearned({
  bullets,
  className,
}: WhatTheAiLearnedProps) {
  const composed = [
    'flex flex-col gap-gutter-md rounded-signal-lg border border-surface-border bg-surface-base p-gutter-lg shadow-signal-card',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <section
      aria-labelledby="what-the-ai-learned-title"
      data-testid="what-the-ai-learned"
      className={composed}
    >
      <header className="flex flex-col gap-gutter-sm">
        <h3
          id="what-the-ai-learned-title"
          className="text-signal-lg font-semibold text-text-primary"
        >
          What This Clinic's AI Has Learned
        </h3>
        <p className="text-signal-sm text-text-secondary">
          Three clinic-specific learnings. Pulled from override and
          outcome telemetry.
        </p>
      </header>

      {bullets.length === 0 ? (
        <p
          data-testid="what-the-ai-learned-empty"
          className="rounded-signal-md border border-dashed border-surface-border bg-surface-muted px-gutter-md py-gutter-lg text-signal-sm text-text-secondary"
        >
          No learnings captured yet for this clinic.
        </p>
      ) : (
        <ul
          role="list"
          aria-label="Clinic-specific learnings"
          data-testid="what-the-ai-learned-list"
          className="flex flex-col gap-gutter-md"
        >
          {bullets.map((bullet, idx) => (
            <li
              key={`${bullet.category}-${idx}`}
              role="listitem"
              data-testid={`learned-bullet-${bullet.category}`}
              data-category={bullet.category}
              className="flex flex-col gap-gutter-sm rounded-signal-md border border-surface-border bg-surface-muted px-gutter-md py-gutter-sm"
            >
              <span className={CATEGORY_CLASSES[bullet.category]}>
                {CATEGORY_LABEL[bullet.category]}
              </span>
              <p className="text-signal-sm text-text-primary">{bullet.text}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
